import { useState, useEffect, useRef } from "react";
import { cn } from "@/lib/utils";
import {
  PenTool, GitBranch, Columns3, CalendarDays, FileText, Users,
  ClipboardList, ShieldCheck, FlaskConical, TestTube2, Zap,
  PackageOpen, BookMarked, Wrench, Table2, GraduationCap,
  BookOpen, Bot, TrendingUp, BarChart3, Sparkles, BriefcaseBusiness,
  Truck, CalendarRange,
} from "lucide-react";

type Tab =
  | "diagram" | "stories" | "kanban" | "timeline" | "velocity" | "analysis"
  | "documents" | "stakeholders" | "requirements" | "compliance" | "agent-log"
  | "knowledge-base" | "team" | "schedule" | "sharing" | "activity" | "test-cases" | "test-runner"
  | "impact-analysis" | "case-studies" | "genius-labs" | "handover"
  | "job-intelligence" | "evolution-log" | "capa" | "suppliers" | "traceability"
  | "decisions" | "risks" | "meeting-notes" | "sprints" | "okr" | "brain" | "catch-up"
  | "charter" | "budget-evm" | "wbs" | "critical-path"
  | "gap-analysis" | "data-dictionary" | "use-cases" | "roadmap";

interface FlowTab {
  id: Tab;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
}

interface Stage {
  id: string;
  label: string;
  color: string;        // tailwind bg class for accent
  textColor: string;
  tabs: FlowTab[];
}

const STAGES: Stage[] = [
  {
    id: "define", label: "Define", color: "bg-blue-500", textColor: "text-blue-600 dark:text-blue-400",
    tabs: [
      { id: "requirements", label: "Requirements", icon: ClipboardList },
      { id: "stakeholders",  label: "Stakeholders",  icon: Users },
      { id: "compliance",    label: "Compliance",    icon: ShieldCheck },
    ],
  },
  {
    id: "plan", label: "Plan", color: "bg-violet-500", textColor: "text-violet-600 dark:text-violet-400",
    tabs: [
      { id: "diagram",   label: "Diagram",      icon: PenTool },
      { id: "documents", label: "Documents",    icon: FileText },
      { id: "stories",   label: "User Stories", icon: GitBranch },
    ],
  },
  {
    id: "build", label: "Build", color: "bg-amber-500", textColor: "text-amber-600 dark:text-amber-400",
    tabs: [
      { id: "kanban",   label: "Board",    icon: Columns3 },
      { id: "timeline", label: "Timeline", icon: CalendarDays },
      { id: "team",     label: "Team",     icon: Users },
    ],
  },
  {
    id: "verify", label: "Verify", color: "bg-orange-500", textColor: "text-orange-600 dark:text-orange-400",
    tabs: [
      { id: "test-cases",      label: "Test Cases",      icon: FlaskConical },
      { id: "test-runner",     label: "Test Runner",     icon: TestTube2 },
      { id: "impact-analysis", label: "Impact Analysis", icon: Zap },
    ],
  },
  {
    id: "quality", label: "Quality", color: "bg-rose-500", textColor: "text-rose-600 dark:text-rose-400",
    tabs: [
      { id: "capa",          label: "CAPA",          icon: Wrench },
      { id: "traceability",  label: "Traceability",  icon: Table2 },
      { id: "evolution-log", label: "Evolution Log", icon: BookMarked },
    ],
  },
  {
    id: "deliver", label: "Deliver", color: "bg-emerald-500", textColor: "text-emerald-600 dark:text-emerald-400",
    tabs: [
      { id: "handover",       label: "Handover",       icon: PackageOpen },
      { id: "case-studies",   label: "Case Studies",   icon: GraduationCap },
      { id: "knowledge-base", label: "Knowledge Base", icon: BookOpen },
    ],
  },
];

const SUPPORT: FlowTab[] = [
  { id: "velocity",         label: "Velocity",    icon: TrendingUp },
  { id: "analysis",         label: "Analysis",    icon: BarChart3 },
  { id: "agent-log",        label: "Agent Log",   icon: Bot },
  { id: "suppliers",        label: "Suppliers",   icon: Truck },
  { id: "genius-labs",      label: "Genius Labs", icon: Sparkles },
  { id: "job-intelligence", label: "Job Intel",   icon: BriefcaseBusiness },
  { id: "schedule",         label: "Schedule",    icon: CalendarRange },
];

// Which tabs are connected to each other (bi-directional)
const CONNECTIONS: Partial<Record<Tab, Tab[]>> = {
  requirements:    ["stories", "compliance", "test-cases", "traceability", "stakeholders", "impact-analysis", "evolution-log"],
  stakeholders:    ["requirements", "documents", "compliance"],
  compliance:      ["requirements", "stakeholders", "capa", "traceability", "suppliers"],
  diagram:         ["stories", "documents"],
  documents:       ["stakeholders", "diagram", "handover", "knowledge-base"],
  stories:         ["requirements", "diagram", "kanban", "timeline", "test-cases", "traceability"],
  kanban:          ["stories", "timeline", "velocity"],
  timeline:        ["stories", "kanban", "team", "velocity", "schedule"],
  team:            ["timeline", "schedule"],
  "test-cases":    ["stories", "requirements", "test-runner", "traceability"],
  "test-runner":   ["test-cases", "capa"],
  "impact-analysis": ["requirements", "stories", "capa"],
  capa:            ["compliance", "test-runner", "impact-analysis", "traceability", "suppliers"],
  traceability:    ["requirements", "stories", "test-cases", "compliance", "capa", "evolution-log"],
  "evolution-log": ["requirements", "traceability", "capa"],
  handover:        ["documents", "knowledge-base", "case-studies"],
  "case-studies":  ["handover", "knowledge-base"],
  "knowledge-base":["documents", "handover", "case-studies", "agent-log"],
  velocity:        ["kanban", "timeline"],
  analysis:        ["requirements", "impact-analysis"],
  "agent-log":     ["knowledge-base"],
  suppliers:       ["compliance", "capa"],
  "genius-labs":   [],
  "job-intelligence": [],
  schedule:        ["team", "timeline"],
};

type Faction = "leadership" | "technical" | "governance";

interface GamePiece {
  role: string;
  abbr: string;
  fg: string;
  bg: string;
  faction: Faction;
}

// Faction border + glow colours — muted, editorial
const FACTION_BORDER: Record<Faction, string> = {
  leadership: "rgba(255,255,255,0.55)",
  technical:  "rgba(80,110,160,0.70)",
  governance: "rgba(160,90,90,0.70)",
};
const FACTION_GLOW: Record<Faction, string> = {
  leadership: "rgba(180,140,100,0.25)",
  technical:  "rgba(70,110,180,0.22)",
  governance: "rgba(150,80,80,0.22)",
};

// Soft jewel-tone palette — desaturated, warm, readable on dark bg
const STAGE_PEOPLE: Record<string, GamePiece[]> = {
  define: [
    { role: "Business Analyst",     abbr: "BA",  fg: "#e8e4dc", bg: "#3B5EA6", faction: "technical"  },
    { role: "Client / Sponsor",     abbr: "CS",  fg: "#1a1510", bg: "#C4993A", faction: "leadership" },
    { role: "Compliance Officer",   abbr: "CO",  fg: "#e8e4dc", bg: "#6B5499", faction: "governance" },
    { role: "Subject Matter Expert",abbr: "SME", fg: "#e8e4dc", bg: "#3D7A8A", faction: "technical"  },
  ],
  plan: [
    { role: "Product Owner",        abbr: "PO",  fg: "#1a1510", bg: "#D4C98A", faction: "leadership" },
    { role: "Business Analyst",     abbr: "BA",  fg: "#e8e4dc", bg: "#3B5EA6", faction: "technical"  },
    { role: "Solution Architect",   abbr: "SA",  fg: "#c8c4bc", bg: "#252535", faction: "technical"  },
    { role: "Scrum Master",         abbr: "SM",  fg: "#e8e4dc", bg: "#A05A3A", faction: "leadership" },
  ],
  build: [
    { role: "Dev Team",             abbr: "DEV", fg: "#0f1a14", bg: "#5A9E80", faction: "technical"  },
    { role: "Scrum Master",         abbr: "SM",  fg: "#e8e4dc", bg: "#A05A3A", faction: "leadership" },
    { role: "Project Manager",      abbr: "PM",  fg: "#e8e4dc", bg: "#8A4A72", faction: "leadership" },
    { role: "Business Analyst",     abbr: "BA",  fg: "#e8e4dc", bg: "#3B5EA6", faction: "technical"  },
  ],
  verify: [
    { role: "QA Lead",              abbr: "QA",  fg: "#1a1510", bg: "#B8A640", faction: "governance" },
    { role: "Tester",               abbr: "TST", fg: "#e8e4dc", bg: "#3D7A8A", faction: "technical"  },
    { role: "Business Analyst",     abbr: "BA",  fg: "#e8e4dc", bg: "#3B5EA6", faction: "technical"  },
    { role: "Developer",            abbr: "DEV", fg: "#0f1a14", bg: "#5A9E80", faction: "technical"  },
  ],
  quality: [
    { role: "Quality Manager",      abbr: "QM",  fg: "#e8e4dc", bg: "#8A4060", faction: "governance" },
    { role: "ISO Officer",          abbr: "ISO", fg: "#e8e4dc", bg: "#7A3030", faction: "governance" },
    { role: "Business Analyst",     abbr: "BA",  fg: "#e8e4dc", bg: "#3B5EA6", faction: "technical"  },
    { role: "Process Owner",        abbr: "PO",  fg: "#1a1510", bg: "#B07830", faction: "governance" },
  ],
  deliver: [
    { role: "Project Manager",      abbr: "PM",  fg: "#e8e4dc", bg: "#8A4A72", faction: "leadership" },
    { role: "Client / Sponsor",     abbr: "CS",  fg: "#1a1510", bg: "#C4993A", faction: "leadership" },
    { role: "Support Lead",         abbr: "SL",  fg: "#e8e4dc", bg: "#6B5499", faction: "leadership" },
    { role: "End User",             abbr: "EU",  fg: "#0f1a14", bg: "#4A8A7A", faction: "leadership" },
  ],
};

function getConnected(tabId: Tab): Set<Tab> {
  const set = new Set<Tab>();
  set.add(tabId);
  (CONNECTIONS[tabId] || []).forEach(t => set.add(t));
  return set;
}

function stageForTab(tabId: Tab): string | null {
  for (const stage of STAGES) {
    if (stage.tabs.some(t => t.id === tabId)) return stage.id;
  }
  return null;
}

const ALL_RIGHT_TABS: FlowTab[] = [
  ...STAGES.flatMap(s => s.tabs),
  ...SUPPORT,
];

interface Props {
  activeTab: Tab;
  onNavigate: (tab: Tab) => void;
}

// Demo sequence: show connections for these tabs in order, then stop
const DEMO_SEQUENCE: Tab[] = ["traceability", "requirements", "capa"];

export default function ProjectCrosswalkView({ activeTab, onNavigate }: Props) {
  const [hoveredRight, setHoveredRight] = useState<Tab | null>(null);
  const [selectedRight, setSelectedRight] = useState<Tab | null>(null);
  const [hoveredStage, setHoveredStage] = useState<string | null>(null);
  const [demoTab, setDemoTab] = useState<Tab | null>(DEMO_SEQUENCE[0]);
  const [isDemo, setIsDemo] = useState(true);
  const demoIndexRef = useRef(0);

  useEffect(() => {
    if (!isDemo) return;
    const interval = setInterval(() => {
      demoIndexRef.current += 1;
      if (demoIndexRef.current >= DEMO_SEQUENCE.length) {
        setDemoTab(null);
        setIsDemo(false);
        clearInterval(interval);
      } else {
        setDemoTab(DEMO_SEQUENCE[demoIndexRef.current]);
      }
    }, 1400);
    return () => clearInterval(interval);
  }, [isDemo]);

  const stopDemo = () => { setIsDemo(false); setDemoTab(null); };

  const focus = selectedRight ?? hoveredRight ?? (isDemo ? demoTab : null);
  const connected = focus ? getConnected(focus) : null;

  // Stage hover: highlight all tabs in that stage
  const stageHighlighted = hoveredStage
    ? new Set(STAGES.find(s => s.id === hoveredStage)?.tabs.map(t => t.id) ?? [])
    : null;

  const highlights = stageHighlighted ?? connected;

  // Derive which stage is in focus from the selected/hovered tool
  const focusedStageId = focus ? stageForTab(focus) : hoveredStage;

  return (
    <div className="flex h-full w-full overflow-hidden">
      {/* LEFT: Stage pipeline — visual roadmap strip, not a tab list */}
      <div className="w-52 shrink-0 border-r border-border overflow-y-auto py-4 px-3" style={{ scrollbarWidth: "thin" }}>
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-4">Workflow Stages</p>

        {/* Vertical pipeline */}
        <div className="relative pl-5">
          {/* Connecting spine */}
          <div className="absolute left-[9px] top-2 bottom-2 w-px bg-border" />

          {STAGES.map((stage, idx) => {
            const stageTabIds = stage.tabs.map(t => t.id);
            const isActive = highlights ? stageTabIds.some(id => highlights.has(id)) : false;
            const isFocused = focusedStageId === stage.id;

            return (
              <div
                key={stage.id}
                className="relative mb-0 cursor-pointer group"
                onMouseEnter={() => { stopDemo(); setHoveredStage(stage.id); }}
                onMouseLeave={() => setHoveredStage(null)}
              >
                {/* Node on spine */}
                <div className={cn(
                  "absolute -left-5 top-3 h-[18px] w-[18px] rounded-full border-2 flex items-center justify-center transition-all duration-150 z-10",
                  isFocused
                    ? `${stage.color} border-transparent shadow-md`
                    : isActive
                    ? "border-primary bg-background"
                    : "border-border bg-background group-hover:border-muted-foreground/60"
                )}>
                  <span className={cn(
                    "text-[7px] font-black tabular-nums",
                    isFocused ? "text-white" : isActive ? "text-primary" : "text-muted-foreground"
                  )}>
                    {idx + 1}
                  </span>
                </div>

                {/* Stage card */}
                <div className={cn(
                  "ml-1 mb-3 px-3 py-2.5 rounded-md border transition-all duration-150",
                  isFocused
                    ? "border-foreground/50 bg-foreground/5 shadow-sm"
                    : isActive
                    ? "border-primary/40 bg-primary/5"
                    : "border-transparent hover:border-border hover:bg-muted/20"
                )}>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className={cn("h-2 w-2 rounded-full shrink-0", stage.color)} />
                    <span className={cn(
                      "text-[11px] font-black uppercase tracking-wider",
                      isFocused || isActive ? stage.textColor : "text-foreground"
                    )}>
                      {stage.label}
                    </span>
                  </div>
                  {/* Mini tool chips */}
                  <div className="flex flex-wrap gap-1">
                    {stage.tabs.map(tab => {
                      const lit = highlights ? highlights.has(tab.id) : false;
                      return (
                        <span
                          key={tab.id}
                          onClick={(e) => { e.stopPropagation(); onNavigate(tab.id); }}
                          className={cn(
                            "text-[8.5px] font-medium px-1 py-0.5 rounded cursor-pointer transition-colors",
                            lit
                              ? "bg-accent/25 text-accent-foreground"
                              : activeTab === tab.id
                              ? "bg-primary/20 text-primary"
                              : "bg-muted/60 text-muted-foreground hover:text-foreground"
                          )}
                        >
                          {tab.label}
                        </span>
                      );
                    })}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Support node */}
          <div className="relative group">
            <div className="absolute -left-5 top-3 h-[18px] w-[18px] rounded-full border-2 border-dashed border-border bg-background flex items-center justify-center z-10">
              <span className="text-[7px] font-black text-muted-foreground">+</span>
            </div>
            <div className="ml-1 px-3 py-2.5 rounded-md border border-transparent hover:border-border hover:bg-muted/20 transition-all duration-150">
              <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Support</span>
              <div className="flex flex-wrap gap-1 mt-1.5">
                {SUPPORT.map(tab => (
                  <span
                    key={tab.id}
                    onClick={() => onNavigate(tab.id)}
                    className={cn(
                      "text-[8.5px] font-medium px-1 py-0.5 rounded cursor-pointer",
                      activeTab === tab.id
                        ? "bg-primary/20 text-primary"
                        : "bg-muted/60 text-muted-foreground hover:text-foreground"
                    )}
                  >
                    {tab.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* MIDDLE: All tabs as flat skill list */}
      <div className="flex-1 overflow-y-auto py-4 px-4" style={{ scrollbarWidth: "thin" }}>
        <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-1">
          Skills & Tools
        </p>
        <div className="flex items-center gap-2 mb-4">
          <p className="text-[10px] text-muted-foreground">
            Click to see which workflow stages it touches · hover to preview
          </p>
          {isDemo && (
            <span className="flex items-center gap-1 text-[10px] text-violet-500 font-mono animate-pulse">
              <span className="h-1.5 w-1.5 rounded-full bg-violet-500 inline-block" />
              demo
            </span>
          )}
        </div>

        {/* Group by stage on right too, but flat feel */}
        {STAGES.map(stage => (
          <div key={stage.id} className="mb-4">
            <p className={cn("text-[10px] font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1.5", stage.textColor)}>
              <span className={cn("inline-block h-1.5 w-1.5 rounded-full", stage.color)} />
              {stage.label}
            </p>
            <div className="space-y-1">
              {stage.tabs.map(tab => {
                const Icon = tab.icon;
                const isSelected = selectedRight === tab.id;
                const isHovered = hoveredRight === tab.id;
                const isActive = activeTab === tab.id;
                const connectedIds = getConnected(tab.id);
                const connCount = connectedIds.size - 1; // exclude self

                return (
                  <button
                    key={tab.id}
                    onClick={() => { stopDemo(); setSelectedRight(isSelected ? null : tab.id); }}
                    onMouseEnter={() => { stopDemo(); setHoveredRight(tab.id); }}
                    onMouseLeave={() => setHoveredRight(null)}
                    className={cn(
                      "w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md border text-left transition-all duration-200",
                      isSelected || (isDemo && demoTab === tab.id)
                        ? "border-primary bg-primary/10 text-primary shadow-sm"
                        : isHovered
                        ? "border-accent/50 bg-accent/10 text-foreground"
                        : isActive
                        ? "border-primary/30 bg-primary/5 text-primary"
                        : "border-border hover:border-muted-foreground/30 hover:bg-muted/30 text-foreground"
                    )}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className={cn("h-3.5 w-3.5 shrink-0", isSelected ? "text-primary" : "text-muted-foreground")} />
                      <span className="text-[12px] font-medium truncate">{tab.label}</span>
                    </div>
                    {connCount > 0 && (
                      <span className={cn(
                        "text-[10px] font-mono shrink-0 px-1.5 py-0.5 rounded",
                        isSelected ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                      )}>
                        {connCount} links
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        ))}

        {/* Support on right */}
        <div className="mb-4">
          <p className="text-[10px] font-semibold uppercase tracking-wider mb-1.5 flex items-center gap-1.5 text-muted-foreground">
            <span className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />
            Support
          </p>
          <div className="space-y-1">
            {SUPPORT.map(tab => {
              const Icon = tab.icon;
              const isSelected = selectedRight === tab.id;
              const isHovered = hoveredRight === tab.id;
              const isActive = activeTab === tab.id;
              const connCount = getConnected(tab.id).size - 1;
              return (
                <button
                  key={tab.id}
                  onClick={() => { stopDemo(); setSelectedRight(isSelected ? null : tab.id); }}
                  onMouseEnter={() => { stopDemo(); setHoveredRight(tab.id); }}
                  onMouseLeave={() => setHoveredRight(null)}
                  className={cn(
                    "w-full flex items-center justify-between gap-3 px-3 py-2 rounded-md border text-left transition-all duration-200",
                    isSelected || (isDemo && demoTab === tab.id)
                    ? "border-primary bg-primary/10 text-primary shadow-sm"
                    : isHovered ? "border-accent/50 bg-accent/10 text-foreground"
                    : isActive ? "border-primary/30 bg-primary/5 text-primary"
                    : "border-border hover:border-muted-foreground/30 hover:bg-muted/30 text-foreground"
                  )}
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className={cn("h-3.5 w-3.5 shrink-0", isSelected ? "text-primary" : "text-muted-foreground")} />
                    <span className="text-[12px] font-medium truncate">{tab.label}</span>
                  </div>
                  {connCount > 0 && (
                    <span className={cn(
                      "text-[10px] font-mono shrink-0 px-1.5 py-0.5 rounded",
                      isSelected ? "bg-primary/20 text-primary" : "bg-muted text-muted-foreground"
                    )}>
                      {connCount} links
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Connection explanation when selected */}
        {selectedRight && (() => {
          const conn = Array.from(getConnected(selectedRight)).filter(t => t !== selectedRight);
          if (conn.length === 0) return null;
          return (
            <div className="mt-4 p-3 rounded-md bg-muted/40 border border-border">
              <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground mb-2">
                Connected to {ALL_RIGHT_TABS.find(t => t.id === selectedRight)?.label}
              </p>
              <div className="flex flex-wrap gap-1.5">
                {conn.map(tabId => {
                  const tab = ALL_RIGHT_TABS.find(t => t.id === tabId);
                  const stageId = stageForTab(tabId);
                  const stage = STAGES.find(s => s.id === stageId);
                  if (!tab) return null;
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tabId}
                      onClick={() => setSelectedRight(tabId)}
                      className={cn(
                        "flex items-center gap-1 px-2 py-0.5 rounded text-[11px] border transition-colors",
                        stage ? `border-border hover:bg-muted/50` : "border-border hover:bg-muted/50"
                      )}
                    >
                      {stage && <span className={cn("h-1.5 w-1.5 rounded-full shrink-0", stage.color)} />}
                      <Icon className="h-3 w-3 text-muted-foreground" />
                      {tab.label}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      {/* RIGHT: People — brutal-neo game pieces */}
      <div
        className="w-52 shrink-0 border-l border-border overflow-y-auto py-4 px-3"
        style={{ scrollbarWidth: "thin", background: "linear-gradient(180deg,#111118 0%,#18181f 100%)" }}
      >
        <p className="text-[10px] font-mono uppercase tracking-widest text-neutral-500 mb-1">People</p>
        <p className="text-[10px] text-neutral-600 mb-4">
          {focusedStageId ? `↳ ${STAGES.find(s => s.id === focusedStageId)?.label} stage` : "hover stage or tool"}
        </p>

        {STAGES.map(stage => {
          const pieces = STAGE_PEOPLE[stage.id] ?? [];
          const isActive = focusedStageId === stage.id;
          const isDimmed = !!focusedStageId && !isActive;

          return (
            <div
              key={stage.id}
              className="mb-5 transition-opacity duration-200"
              style={{ opacity: isDimmed ? 0.12 : 1 }}
            >
              <p className="text-[9px] font-black uppercase tracking-widest mb-2 flex items-center gap-1"
                style={{ color: isActive ? "#fff" : "#404050" }}>
                <span
                  className="inline-block h-1.5 w-1.5 rounded-full"
                  style={{ background: isActive ? "#fff" : "#303040" }}
                />
                {stage.label}
              </p>
              <div className="grid grid-cols-2 gap-2">
                {pieces.map(piece => {
                  const bdr = FACTION_BORDER[piece.faction];
                  const glow = FACTION_GLOW[piece.faction];
                  const isGov = piece.faction === "governance";
                  return (
                    <div
                      key={piece.abbr + piece.role}
                      className="relative flex flex-col items-center justify-center px-1 py-2.5 gap-1 cursor-default select-none transition-all duration-[120ms]"
                      style={{
                        borderRadius: 14,
                        border: isGov
                          ? `2px dashed ${isActive ? bdr : "#28202a"}`
                          : `2px solid ${isActive ? bdr : "#22222e"}`,
                        background: isActive ? piece.bg : "#1c1c24",
                        boxShadow: isActive
                          ? `4px 4px 0px rgba(0,0,0,0.6), 0 0 12px ${glow}`
                          : "2px 2px 0px rgba(0,0,0,0.4)",
                        transform: isActive ? "translate(-1px,-1px)" : "none",
                      }}
                    >
                      {/* Knob */}
                      <div
                        className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 rounded-full"
                        style={{
                          background: isActive ? piece.bg : "#1c1c24",
                          border: `2px solid ${isActive ? bdr : "#22222e"}`,
                          boxShadow: isActive ? `0 0 6px ${glow}` : "none",
                        }}
                      />
                      {/* Abbr */}
                      <span
                        className="text-[11px] leading-none mt-1"
                        style={{
                          fontFamily: '"Space Grotesk", "Source Sans 3", sans-serif',
                          fontWeight: 800,
                          letterSpacing: "0.08em",
                          color: isActive ? piece.fg : "#303048",
                        }}
                      >
                        {piece.abbr}
                      </span>
                      {/* Role */}
                      <span
                        className="text-[7.5px] font-bold uppercase text-center leading-tight px-0.5"
                        style={{ color: isActive ? piece.fg + "bb" : "#252535" }}
                      >
                        {piece.role}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Faction legend */}
        <div className="mt-6 pt-4 border-t border-neutral-800 space-y-1.5">
          {(["leadership","technical","governance"] as Faction[]).map(f => (
            <div key={f} className="flex items-center gap-2">
              <div
                className="h-2.5 w-2.5 rounded-sm shrink-0"
                style={{
                  border: f === "governance" ? "2px dashed #FF2200" : `2px solid ${FACTION_BORDER[f]}`,
                  background: "transparent",
                }}
              />
              <span className="text-[8.5px] font-bold uppercase tracking-wider" style={{ color: "#404050" }}>
                {f}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
